/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { Amount } from '@api';
import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dispatch } from 'redux';
import { getCheckoutModelFromBackend, showSyncErrors } from 'src/js/enrol/actions/Actions';
import CheckoutApiService from 'src/js/enrol/services/CheckoutApiService';
import { resetPaymentState, setPaymentFetching } from '../actions/Actions';

interface Props {
  amount: Amount;
  disabled?: boolean;
  dispatch: Dispatch<any>;
  checkoutProcessEwayCCPayment: (eWaySecureFieldCode: string) => void;
}

const validationCodes = {
  "V6021": "Cardholder Name Required",
  "V6022": "Valid Card Number Required",
  "V6023": "Valid Card CVN Required",
  "V6033": "Invalid Expiry Date",
};

const rowStyle = "width: 300px; margin-bottom: 16px";

const cardStyles = `
    font: inherit;
    letter-spacing: inherit;
    font-weight: 400;
    font-size: 16px;
    box-sizing: content-box;
    height: 8px;
    -webkit-tap-highlight-color: transparent;
    animation-name: mui-auto-fill-cancel;
    animation-duration: 10ms;
    padding: 16.5px 14px;
    letter-spacing: 0.00938em;
    cursor: text;
    -webkit-box-align: center;
    align-items: center;
    position: relative;
    border-radius: 0px;
    box-shadow: none;
`

const EwayPaymentComp = (
  {
    checkoutProcessEwayCCPayment, dispatch, amount, disabled
  }: Props) => {

  const [publicApiKey, setPublicApiKey] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    CheckoutApiService.getClientPreferences()
      .then(res => setPublicApiKey(res.clientKey))
  }, []);

  const secureCodeRef = useRef<string>(null);

  const groupFieldConfig = useMemo(() => ({
    publicApiKey,
    fieldDivId: "eway-secure-panel",
    fieldType: "group",
    layout : {
      rows : [ {
        styles: rowStyle,
        cells:  [
          {
            colSpan: 12,
            field: { fieldType: "name", fieldColSpan: 12,  styles: cardStyles, autocomplete: "true" }
          }
        ],
      },
        {
          styles: rowStyle,
          cells: [
            {
              colSpan: 12,
              field: { fieldType: "card", fieldColSpan: 12,  styles: cardStyles, autocomplete: "true" }
            }
          ]
        },
        {
          styles: rowStyle,
          cells: [
            {
              styles: "width: 127px",
              colSpan: 6,
              field: {  fieldType: "expirytext", fieldColSpan: 12, styles: cardStyles, autocomplete: "true" }
            },
            {
              styles: "width: 127px; float: right;",
              colSpan: 6,
              field: {  fieldType: "cvn", fieldColSpan: 12,  styles: cardStyles, autocomplete: "true" }
            }
          ]
        },
      ]
    }
  }), [publicApiKey]);

  const securePanelCallback = useCallback(event => {
    if (!event.fieldValid) {
      setReady(false);
      dispatch(showSyncErrors({
        message: event.errors.split(" ").reduce((p, c) => p + `${validationCodes[c.trim()] || c}\n`, '')
      }));
    } else {
      setReady(true);
      secureCodeRef.current = event.secureFieldCode;
    }
  }, [secureCodeRef.current]);

  useEffect(() => {
    if ((window as any).eWAY && publicApiKey) {
      (window as any).eWAY.setupSecureField(groupFieldConfig, securePanelCallback);
    }
  }, [publicApiKey]);

  const pymentCallback = () => {
    checkoutProcessEwayCCPayment(secureCodeRef.current);
  };

  const proceedPayment = () => {
    dispatch(resetPaymentState());
    dispatch(getCheckoutModelFromBackend());
  };

  useEffect(() => {
    if (amount.payNow > 0) {
      proceedPayment();
    }
  }, [
    amount.payNow
  ]);

  const handleSubmit = e => {
    dispatch(setPaymentFetching(true));
    e.preventDefault();
    (window as any).eWAY.saveAllFields(pymentCallback, 2000);
    return false;
  };

  return (
    <div className="col text-center">
      <div id="eway-secure-panel" />
      <button
        disabled={!ready || disabled}
        className={classnames('btn btn--primary', { disabled })}
        id="enterCardDetails"
        name="enterCardDetails"
        onClick={handleSubmit}
      >
        Finalize checkout
      </button>
    </div>
  );
};

export default EwayPaymentComp;